
import { Chain, formatUnits, PublicClient } from "viem"
import { TokenBalance } from "@/Models/Balance"
import { ExtendedNetwork, NetworkTypes, ExtendedToken, getNativeToken } from "@/Models/Network"
import { createConfig } from '@wagmi/core'
import { erc20Abi } from 'viem'
import { multicall } from '@wagmi/core'
import { getBalance, GetBalanceReturnType } from '@wagmi/core'
import resolveChain, { isContractNativeToken } from "@/lib/resolveChain"
import BalanceGetterAbi from "@/lib/abis/BALANCEGETTERABI.json"
import KnownInternalNames from "@/lib/knownIds"
import { BalanceProvider } from "@/Models/BalanceProvider"
import { buildNetworkTransport } from "@/lib/rpc/resolveNetworkRpcUrl"

export class EVMBalanceProvider extends BalanceProvider {
    supportsNetwork: BalanceProvider['supportsNetwork'] = (network) => {
        return network.networkType === NetworkTypes.EVM && !!getNativeToken(network)
    }

    fetchBalance: BalanceProvider['fetchBalance'] = async (address, network, options) => {
        if (!network) return
        const chain = resolveChain(network)
        if (!chain) throw new Error("Could not resolve chain")

        try {
            const balances = await this.contractGetBalances(address, chain, network, options)
            return balances
        } catch (e) {
            // console.log(e)
        }

        const balances = await this.getBalances(address, chain, network, options)

        return balances
    }

    getBalances = async (address: string, chain: Chain, network: ExtendedNetwork, options?: { timeoutMs?: number, retryCount?: number }): Promise<TokenBalance[] | undefined> => {
        try {
            const { createPublicClient } = await import("viem")
            const publicClient: PublicClient = createPublicClient({
                chain,
                transport: buildNetworkTransport(network, {
                    timeout: options?.timeoutMs,
                    retryCount: options?.retryCount,
                }),
            })

            const erc20Promise = getErc20Balances({
                address,
                assets: network.tokens,
                network,
                publicClient,
                hasMulticall: !!network.contracts?.find(c => c.type === "Multicall"),
                timeoutMs: options?.timeoutMs,
                retryCount: options?.retryCount,
            })

            const nativeToken = getNativeToken(network)
            // On contract-native networks (e.g. Tempo) eth_getBalance returns garbage —
            // read the native token via ERC-20 balanceOf instead.
            const nativeContract = isContractNativeToken(network)
                ? (network.nativeTokenAddress as `0x${string}`)
                : undefined
            const nativePromise = nativeToken
                ? getTokenBalance(address as `0x${string}`, network, nativeContract, options?.timeoutMs, options?.retryCount)
                : Promise.resolve(null)

            const [erc20BalancesContractRes, nativeBalanceData] = await Promise.all([
                erc20Promise,
                nativePromise,
            ])

            const balances = (erc20BalancesContractRes && this.resolveERC20Balances(
                erc20BalancesContractRes,
                network
            )) || []

            const nativeBalance = (nativeToken && nativeBalanceData) && this.resolveBalance(network, nativeToken, nativeBalanceData)
            let res: TokenBalance[] = []
            return res.concat(balances, nativeBalance ? [nativeBalance] : [])
        }
        catch (e) {
            throw e
        }
    }

    contractGetBalances = async (address: string, chain: Chain, network: ExtendedNetwork, options?: { timeoutMs?: number, retryCount?: number }): Promise<TokenBalance[] | null> => {
        if (!network) throw new Error("Network is required for contract get balances")

        const { createPublicClient } = await import("viem")
        const publicClient = createPublicClient({
            chain,
            transport: buildNetworkTransport(network, {
                timeout: options?.timeoutMs,
                retryCount: options?.retryCount,
            }),
        })

        const contract = balanceGetterContracts.find(c => c.networks.includes(network.caip2Id))
        if (!contract) throw new Error(`No contract found for network ${network.caip2Id}`)

        const erc20Tokens = network.tokens?.filter(a => a.contract !== network.nativeTokenAddress)
        const tokenContracts = erc20Tokens.map(a => a.contract as `0x${string}`)

        const balances = await publicClient.readContract({
            address: contract.address,
            abi: BalanceGetterAbi,
            functionName: 'getBalances',
            args: [address as `0x${string}`, tokenContracts]
        }) as [string[], number[]]

        const resolvedERC20Balances = erc20Tokens.map((token, index) => {
            const amount = balances[1][index]

            if (amount >= 0) {
                const formattedAmount = Number(formatUnits(BigInt(amount), token.decimals))
                return {
                    network: network.caip2Id,
                    token: token.symbol,
                    amount: formattedAmount,
                    request_time: new Date().toJSON(),
                    decimals: token.decimals,
                    isNativeCurrency: false,
                }
            }
        })

        const nativeToken = getNativeToken(network)
        const nativeTokenBalance = Number(balances?.[1]?.[balances?.[1]?.length - 1])

        const nativeTokenResolvedBalance: TokenBalance | undefined = nativeToken?.decimals ? {
            network: network.caip2Id,
            token: nativeToken.symbol,
            amount: nativeTokenBalance >= 0 ? Number(formatUnits(BigInt(nativeTokenBalance), nativeToken.decimals)) : undefined,
            request_time: new Date().toJSON(),
            decimals: nativeToken.decimals,
            isNativeCurrency: true,
        } : undefined

        const res = [...resolvedERC20Balances, nativeTokenResolvedBalance].filter((b): b is TokenBalance => b !== undefined)

        return res
    }

    resolveERC20Balances = (
        multicallRes: ERC20ContractRes[],
        network: ExtendedNetwork,
    ) => {
        const assets = network?.tokens?.filter(a => a.contract !== network.nativeTokenAddress)
        if (!assets)
            return null
        const contractBalances = multicallRes?.map((d, index) => {
            const currency = assets[index]
            if (!d.error) {
                return {
                    network: network.caip2Id,
                    token: currency.symbol,
                    amount: Number(formatUnits(BigInt(d.result as string | number), currency.decimals)),
                    request_time: new Date().toJSON(),
                    decimals: currency.decimals,
                    isNativeCurrency: false,
                }
            } else {
                return this.resolveTokenBalanceFetchError(d.error, currency, network)
            }
        })
        return contractBalances
    }

    resolveBalance = (
        network: ExtendedNetwork,
        token: ExtendedToken,
        balanceData: NativeBalanceResponse
    ) => {

        if (balanceData.error !== null) return this.resolveTokenBalanceFetchError(new Error(balanceData.error), token, network)

        const nativeBalance: TokenBalance = {
            network: network.caip2Id,
            token: token.symbol,
            amount: Number(formatUnits(BigInt(balanceData?.value), token.decimals)),
            request_time: new Date().toJSON(),
            decimals: token.decimals,
            isNativeCurrency: true,
        }

        return nativeBalance
    }

}

export type ERC20ContractRes = ({
    error: Error;
    result?: undefined | null;
    status: "failure";
} | {
    error?: undefined | null;
    result: unknown;
    status: "success";
})

type GetBalanceArgs = {
    address: string,
    network: ExtendedNetwork,
    assets: ExtendedToken[],
    publicClient: PublicClient,
    hasMulticall: boolean,
    timeoutMs?: number,
    retryCount?: number
}

export const getErc20Balances = async ({
    address,
    network,
    assets,
    publicClient,
    hasMulticall = false,
    timeoutMs,
    retryCount
}: GetBalanceArgs): Promise<ERC20ContractRes[] | null> => {

    const erc20Assets = assets?.filter(a => a.contract !== network.nativeTokenAddress)

    const contracts = erc20Assets.map(a => ({
        address: a.contract as `0x${string}`,
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: [address],
    }))

    try {
        if (hasMulticall) {
            const chain = resolveChain(network)
            if (!chain) throw new Error("Could not resolve chain")

            const config = createConfig({
                chains: [chain],
                transports: {
                    [chain.id]: buildNetworkTransport(network, { timeout: timeoutMs, retryCount })
                }
            })

            const contractRes = await multicall(config, {
                chainId: chain.id,
                contracts: contracts
            })

            const failedIndices: number[] = []
            contractRes.forEach((result, index) => {
                if (result.status === 'failure') {
                    failedIndices.push(index)
                }
            })

            if (failedIndices.length > 0) {
                const mutableResults = [...contractRes] as ERC20ContractRes[]

                await Promise.all(failedIndices.map(async (index) => {
                    const contract = contracts[index]
                    try {
                        const balance = await publicClient.readContract({
                            address: contract.address,
                            abi: erc20Abi,
                            functionName: 'balanceOf',
                            args: [address as `0x${string}`]
                        })
                        mutableResults[index] = {
                            status: 'success',
                            result: balance,
                            error: undefined
                        }
                    } catch (e) {
                        mutableResults[index] = {
                            status: 'failure',
                            result: null,
                            error: e instanceof Error ? e : new Error(String(e))
                        }
                    }
                }))

                return mutableResults
            }

            return contractRes
        }
        else {
            const balances: ERC20ContractRes[] = []
            for (let i = 0; i < contracts.length; i++) {
                try {
                    const contract = contracts[i]
                    const balance = await publicClient.readContract({
                        address: contract?.address as `0x${string}`,
                        abi: erc20Abi,
                        functionName: 'balanceOf',
                        args: [address as `0x${string}`]
                    })
                    balances.push({
                        status: "success",
                        result: balance,
                        error: undefined
                    })
                }
                catch (e) {
                    balances.push({
                        status: "failure",
                        result: null,
                        error: e instanceof Error ? e : new Error(String(e))
                    })
                }
            }
            return balances
        }
    }
    catch (e) {
        return null;
    }

}

type NativeBalanceResponse = (GetBalanceReturnType & {
    error: null;
} | {
    error: string
})

export const getTokenBalance = async (address: `0x${string}`, network: ExtendedNetwork, contract?: `0x${string}` | null, timeoutMs?: number, retryCount?: number): Promise<NativeBalanceResponse | null> => {

    try {
        const chain = resolveChain(network)
        if (!chain) throw new Error("Could not resolve chain")
        const config = createConfig({
            chains: [chain],
            transports: {
                [chain.id]: buildNetworkTransport(network, { timeout: timeoutMs, retryCount })
            }
        })

        const res = await getBalance(config, {
            address,
            chainId: chain.id,
            ...(contract ? { token: contract } : {})
        })
        return { error: null, ...res }
    } catch (e) {
        return { error: e }
    }

}


const balanceGetterContracts = [
    {
        address: '0xb65a146b7C2D5BEec6EE8a5F38C467b5A88b26Dc' as `0x${string}`,
        networks: [
            KnownInternalNames.Networks.EthereumMainnet,
            KnownInternalNames.Networks.BaseMainnet,
        ]
    },
    {
        address: '0xf3C74887D68Cd6d6c64Fb9ab24ca6812182eED44' as `0x${string}`,
        networks: [
            KnownInternalNames.Networks.ArbitrumMainnet,
        ]
    }
]
